import { computed } from 'vue'
import type { SelectableHint } from '@/composables/useMeshHighlighting'
import type { PartAllocationRequest, useViewerSelection } from '@/composables/useViewerSelection'
import type { useInstancePicking } from '@/composables/useInstancePicking'
import type { HintInstances } from '@/composables/useHintInstances'
import {
  findInstanceAllocation,
  instanceCountOf,
  needsInstanceSelection,
  type EbomPartInstanceAllocation,
} from '@/utils/instanceAllocation'

type ViewerSelection = ReturnType<typeof useViewerSelection>
type InstancePicking = ReturnType<typeof useInstancePicking>

export type InstanceAllocationActionsDeps = {
  selection: Pick<
    ViewerSelection,
    | 'selectedHint'
    | 'selectedInstanceIndex'
    | 'selectionAllocated'
    | 'preserveThroughNextSync'
    | 'clearSelection'
  >
  instancePicking: Pick<
    InstancePicking,
    'active' | 'target' | 'picked' | 'canSave' | 'start' | 'stop'
  >
  hintInstances: Pick<HintInstances, 'instanceCount'>
  enabled: () => boolean
  selectableHints: () => SelectableHint[]
  allocations: () => EbomPartInstanceAllocation[]
  onAllocatePart: (request: PartAllocationRequest) => void
  onSaveInstances: (hint: SelectableHint, instanceIndexes: number[]) => void
}

export function useInstanceAllocationActions(deps: InstanceAllocationActionsDeps) {
  const { selection, instancePicking } = deps

  const prompt = computed(() => {
    if (!deps.enabled() || instancePicking.active.value) return null
    for (const allocation of deps.allocations()) {
      const hint = deps.selectableHints().find((h) => h.ebomPartId === allocation.ebomPartId)
      if (!hint?.promoted) continue
      const total = instanceCountOf(hint.milliQuantity)
      const required = instanceCountOf(allocation.milliQuantity)
      if (!needsInstanceSelection(allocation, total)) continue
      if (Math.min(deps.hintInstances.instanceCount(hint), total) < required) continue
      return { hint, required, total }
    }
    return null
  })

  function requestAllocation(allInstances: boolean) {
    const hint = selection.selectedHint.value
    if (!hint) return
    selection.preserveThroughNextSync(allInstances || !selection.selectionAllocated.value)
    deps.onAllocatePart({
      hint,
      instanceIndex: selection.selectedInstanceIndex.value,
      allInstances,
    })
  }

  function startPicking(hint: SelectableHint, required: number, initial: number[] = []) {
    selection.clearSelection()
    instancePicking.start(hint, required, initial)
  }

  function pickInstancesFromPrompt() {
    if (prompt.value) startPicking(prompt.value.hint, prompt.value.required)
  }

  function pickInstancesFromSelection() {
    const hint = selection.selectedHint.value
    const allocation = hint ? findInstanceAllocation(deps.allocations(), hint.ebomPartId) : null
    if (!hint || !allocation) return
    const index = selection.selectedInstanceIndex.value
    startPicking(hint, instanceCountOf(allocation.milliQuantity), index === null ? [] : [index])
  }

  function saveInstances() {
    const hint = instancePicking.target.value
    if (!hint || !instancePicking.canSave.value) return
    const indexes = [...instancePicking.picked.value].sort((a, b) => a - b)
    instancePicking.stop()
    deps.onSaveInstances(hint, indexes)
  }

  return {
    prompt,
    allocateSelection: () => requestAllocation(false),
    allocateAllInstances: () => requestAllocation(true),
    pickInstancesFromPrompt,
    pickInstancesFromSelection,
    saveInstances,
  }
}
